import {isEqual} from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProtocolVersion} from 'notebook/ELNConstants';
import getNotePartType from 'notebook/notes/util/getNotePartType';
import {SimpleTableDoc} from 'notebook/simple-tables/useSimpleEvaluation';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {TABLE_OPS} from 'notebook/tables/augmentNotePartTablesWithValues';
import {FullTableMap} from 'notebook/tables/excess/Evaluator';
import {hasLoadingCells} from 'notebook/tables/excess/Util';
import {Cell} from 'notebook/tables/TableConstants';
import {EvaluationSnapshot} from 'notebook/tables/TableStoreTransforms';
import {areTypedValuesEquivalent} from 'notebook/tables/typed-values/DataTypes';
import {isNote, isProtocol, isSimpleTableDoc} from 'notebook/TypePredicates';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import {getStore} from 'redux-stores/Store';
import {NoteLinkedAssayData} from 'runs/AttachmentConstants';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import {StageRun} from 'workflows/WorkflowConstants';

export function getNoteTableMap(fileId: string, note: Note): FullTableMap {
  const fullTableMap: FullTableMap = {};
  note.noteParts.forEach((notePart) => {
    const type = getNotePartType(notePart);
    if (type == null || (!TABLE_OPS[type] && !(type === ModelTypeConstants.NOTE_LINKED_ASSAY_DATA))) {
      return;
    }

    if (type === ModelTypeConstants.NOTE_LINKED_ASSAY_DATA) {
      if ((notePart.item as NoteLinkedAssayData).versionedData != null) {
        for (const structuredTable of (notePart.item as NoteLinkedAssayData).versionedData.structuredTables) {
          const structuredTableAdapter = getStructuredTableAdapters()[structuredTable.kind];
          const table = structuredTableAdapter.toTable(structuredTable);
          fullTableMap[table.name] = table;
        }
      }
    } else {
      // Fetch stage run information for tables in stage entries
      const entryType = APIUtil.apiIdToMaybeType(fileId);
      const isStageEntry =
        entryType === (ModelTypeConstants.STAGE_CONFIGURATION as string) ||
        entryType === (ModelTypeConstants.STAGE_ENTRY as string);
      let stageRuns: Array<StageRun> = [];

      if (isStageEntry) {
        const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);
        stageRuns = workflowSelector.getStageRunsForEntry(fileId);
      }
      const table = TABLE_OPS[type].extractTable(notePart, fileId, stageRuns);
      fullTableMap[table.name] = table;
    }
  });
  return fullTableMap;
}

export function getProtocolTableMap(protocol: ProtocolVersion): FullTableMap {
  const fullTableMap: FullTableMap = {};
  protocol.steps.forEach((step) => {
    if (step.table == null) {
      return;
    }
    fullTableMap[step.table.name] = step.table;
  });
  return fullTableMap;
}

/**
 * Return whether a note runtime snapshot's evaluation snapshot should be used for rendering.
 *
 * If the evaluation snapshot exists, does not have loading indicators, or its underlying dependencies have
 * not changed, then it safe to render from.
 */
export function shouldRenderFromEvaluationSnapshot(
  fileId: string,
  oldItem: Note | null,
  item: Note,
  itemNoteTableMap: FullTableMap
): boolean {
  const {evaluationSnapshot} = item.noteRuntimeSnapshot;

  // If the evaluation snapshot is invalid we should log a warning and investigate
  if (
    evaluationSnapshot != null &&
    !isValidEvaluationSnapshot(evaluationSnapshot, item, fileId, itemNoteTableMap)
  ) {
    // If no old item then this is initial render and we should log a warning for an invalid eval snapshot
    // Dependencies should also not be equal if the evaluation snapshot does not have the valid shape
    if (!oldItem || areEvalSnapshotDepsEqual(fileId, oldItem, item)) {
      logging.warn('Evaluation snapshot is invalid!', {
        extra: {fileId, noteRuntimeSnapshot: item.noteRuntimeSnapshot},
      });
    }
    return false;
  }

  return (
    // On initial render, only evaluate if necessary.
    (!oldItem && evaluationSnapshot != null && !hasLoadingCells(evaluationSnapshot)) ||
    areEvalSnapshotDepsEqual(fileId, oldItem, item, itemNoteTableMap)
  );
}

/*
 * Validate that the evaluation snapshot and the current note have the same shapes for all the tables
 */
export function isValidEvaluationSnapshot(
  evaluationSnapshot: EvaluationSnapshot,
  item: Note,
  fileId: string,
  fullTableMap: FullTableMap
): boolean {
  for (const [tableName, table] of Object.entries(fullTableMap)) {
    if (!evaluationSnapshot.hasOwnProperty(tableName)) {
      return false;
    }
    const evaluatedTable = evaluationSnapshot[tableName];
    if (evaluatedTable.length !== table.table.length) {
      return false;
    }

    for (let i = 0; i < evaluatedTable.length; i++) {
      if (evaluatedTable[i].length !== table.table[i].length) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Compares evaluationSnapshot dependencies to determine whether a re-evaluation is necessary
 */
export function areEvalSnapshotDepsEqual<ItemType extends Note | ProtocolVersion | SimpleTableDoc>(
  fileId: string,
  oldItem: ItemType | null,
  item: ItemType,
  itemNoteTableMap?: FullTableMap
): boolean {
  if (
    oldItem == null ||
    // For bootstrap case (no evaluation snapshot present), we actually would want to run re-evaluation.
    item.noteRuntimeSnapshot.evaluationSnapshot == null ||
    // We check shallow equality since the evaluationSnapshots should point to the same underlying object if identical
    item.noteRuntimeSnapshot !== oldItem.noteRuntimeSnapshot ||
    item.selectors !== oldItem.selectors
  ) {
    return false;
  }

  // Since we build a new table map for each evaluation snapshot, we compare using deep equality.
  if (isNote(item) && isNote(oldItem)) {
    if (itemNoteTableMap == null) {
      itemNoteTableMap = getNoteTableMap(fileId, item);
    }
    return isEqual(getNoteTableMap(fileId, oldItem), itemNoteTableMap);
  } else if (isProtocol(item) && isProtocol(oldItem)) {
    return isEqual(getProtocolTableMap(item), getProtocolTableMap(oldItem));
  } else if (isSimpleTableDoc(item) && isSimpleTableDoc(oldItem)) {
    return isEqual(item.fullTableMap, oldItem.fullTableMap);
  }
  return false;
}

/**
 * Compares two basic tables and returns coordinates that are different
 */
export function computeTableMappedDiffCoordinates(
  oldTable?: Array<Array<Cell>>,
  newTable?: Array<Array<Cell>>
): Array<{rowIndex: number; columnIndex: number}> | null {
  // TODO(BNCH-87545): handle adding and removing rows and columns
  if (oldTable === undefined || newTable === undefined) {
    return null;
  }
  if (oldTable.length !== newTable.length || oldTable[0].length !== newTable[0].length) {
    return null;
  }
  const diffCoordinates: Array<{rowIndex: number; columnIndex: number}> = [];
  oldTable.forEach((row, rowIndex) => {
    row.forEach((cell, columnIndex) => {
      const oldCell = newTable[rowIndex][columnIndex];
      if ((cell.typedValue && !oldCell.typedValue) || (!cell.typedValue && oldCell.typedValue)) {
        diffCoordinates.push({rowIndex, columnIndex});
      } else if (cell.typedValue && oldCell.typedValue) {
        if (
          !areTypedValuesEquivalent(cell.typedValue, oldCell.typedValue) ||
          cell.text !== oldCell.text ||
          cell.mergeColSpan !== oldCell.mergeColSpan ||
          cell.mergeRowSpan !== oldCell.mergeRowSpan ||
          cell.selectorId !== oldCell.selectorId
        ) {
          diffCoordinates.push({rowIndex, columnIndex});
        }
      } else if (
        cell.text !== oldCell.text ||
        cell.mergeColSpan !== oldCell.mergeColSpan ||
        cell.mergeRowSpan !== oldCell.mergeRowSpan ||
        cell.selectorId !== oldCell.selectorId
      ) {
        diffCoordinates.push({rowIndex, columnIndex});
      }
    });
  });
  return diffCoordinates;
}

export function tableProvenanceUuidsForNotePart(notePart: NotePart): Array<string> {
  const type = getNotePartType(notePart);
  if (!type || !TABLE_OPS[type]) {
    return [];
  }
  return TABLE_OPS[type].provenanceUuids(notePart);
}
